import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '../../services/config.service';
import { resolveBffUrl } from '../../utils/bff-url-resolver.util';
import { InternalAuthFlowUtil } from '../../utils/internal-auth-flow.util';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login-page.html',
  styleUrls: ['./login-page.scss']
})
export class LoginPage {
  usuario: string = '';
  senha: string = '';
  novaSenha: string = '';
  confirmarNovaSenha: string = '';

  modoAlteracaoSenha: boolean = false;
  private passwordChangedAt: number | null = null;
  private readonly passwordChangePropagationMs = 3000;

  mostrarSenha: boolean = false;
  usuarioErro: boolean = false;
  usuarioCpfInvalido: boolean = false;
  senhaErro: boolean = false;
  novaSenhaErro: boolean = false;
  confirmarNovaSenhaErro: boolean = false;

  usuarioFocado: boolean = false;
  senhaFocada: boolean = false;
  novaSenhaFocada: boolean = false;
  confirmarNovaSenhaFocada: boolean = false;

  autenticando: boolean = false;
  mensagemAutenticacao: string = '';
  exibirPopupRecuperarSenha: boolean = false;

  backgroundImageUrl: string = 'assets/images/login-car-bg.png';
  logoImageUrl: string = '/assets/images/PLATAFORMA LOGO.svg';

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService
  ) {
    this.applyInitialAuthMessageFromQuery();
  }

  private applyInitialAuthMessageFromQuery(): void {
    const authMessage = this.route.snapshot.queryParamMap.get('authMessage');
    if (authMessage && authMessage.trim().length > 0) {
      this.mensagemAutenticacao = authMessage;
    }
  }

  realizarLogin() {
    if (this.modoAlteracaoSenha) {
      this.realizarAlteracaoSenha();
      return;
    }

    const loginValido = this.validarLogin(this.usuario, this.senha);

    if (!loginValido) {
      this.usuarioErro = true;
      this.usuarioCpfInvalido = false;
      this.senhaErro = true;
      this.mensagemAutenticacao = 'Não foi possível validar o usuário.';
      return;
    }

    this.usuarioErro = false;
    this.usuarioCpfInvalido = false;
    this.senhaErro = false;
    this.mensagemAutenticacao = '';

    this.autenticando = true;
    this.configService.loadPublicConfig().subscribe({
      next: (publicConfig) => {
        const sanitizedApiBaseUrl = String(publicConfig.bancoVwApiBaseUrl || '').replace(/\/+$/, '');
        if (sanitizedApiBaseUrl === '') {
          this.autenticando = false;
          this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
          return;
        }

        const authBaseUrl = sanitizedApiBaseUrl.replace(/\/v1$/i, '');
        const endpoint = `${authBaseUrl}/token`;

        this.http.post<unknown>(endpoint, {
          Usuario: this.usuario,
          Senha: this.senha
        }).subscribe({
          next: (response) => {
            this.autenticando = false;

            const token = this.getTokenFromResponse(response);
            if (!token) {
              this.handleInvalidLogin();
              return;
            }

            const state = InternalAuthFlowUtil.createState();

            this.router.navigate(['/oauth'], {
              queryParams: {
                token,
                screen: 'leads',
                username: this.usuario.trim(),
                state
              }
            });
          },
          error: (error) => {
            if (error?.status === 401 && this.isWithinPasswordChangePropagation()) {
              // Backend pode ainda estar propagando a troca de senha; retentar após breve espera
              setTimeout(() => this.realizarLogin(), this.passwordChangePropagationMs);
              return;
            }

            this.autenticando = false;
            this.handleInvalidLogin();
          }
        });
      },
      error: (error) => {
        this.autenticando = false;
        console.error('❌ [LoginPage] Configuração pública ausente para login.', error);
        this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
      }
    });
  }

  private validarLogin(usuario: string, senha: string): boolean {
    return usuario.trim().length > 0 && senha.trim().length > 0;
  }

  private realizarAlteracaoSenha(): void {
    const usuarioValido = this.usuario.trim().length > 0;
    const senhaAtualValida = this.senha.trim().length > 0;
    const novaSenhaValida = this.novaSenha.trim().length > 0;
    const confirmarNovaSenhaValida = this.confirmarNovaSenha.trim().length > 0;

    this.usuarioErro = !usuarioValido;
    this.senhaErro = !senhaAtualValida;
    this.novaSenhaErro = !novaSenhaValida;
    this.confirmarNovaSenhaErro = !confirmarNovaSenhaValida;

    if (!usuarioValido || !senhaAtualValida || !novaSenhaValida || !confirmarNovaSenhaValida) {
      this.mensagemAutenticacao = 'Preencha todos os campos para alterar a senha.';
      return;
    }

    if (this.novaSenha !== this.confirmarNovaSenha) {
      this.novaSenhaErro = true;
      this.confirmarNovaSenhaErro = true;
      this.mensagemAutenticacao = 'A nova senha e a confirmação devem ser iguais.';
      return;
    }

    this.usuarioErro = false;
    this.senhaErro = false;
    this.novaSenhaErro = false;
    this.confirmarNovaSenhaErro = false;
    this.mensagemAutenticacao = '';

    this.autenticando = true;
    this.configService.loadPublicConfig().subscribe({
      next: (publicConfig) => {
        const bffUrl = String(publicConfig.bffUrl || '').trim();
        if (bffUrl === '') {
          this.autenticando = false;
          this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
          return;
        }

        const enableDebugMode = this.configService.isDebugEnabled();
        const endpoint = `${resolveBffUrl(bffUrl, enableDebugMode)}/api/token/trocarsenha`;

        this.http.post<unknown>(endpoint, {
          Usuario: this.usuario,
          Senha: this.senha,
          NovaSenha: this.novaSenha
        }).subscribe({
          next: () => {
            this.autenticando = false;
            this.passwordChangedAt = Date.now();
            this.cancelarAlteracaoSenha();
            this.mensagemAutenticacao = 'Senha alterada com sucesso.';
          },
          error: () => {
            this.autenticando = false;
            this.modoAlteracaoSenha = true;
            this.mensagemAutenticacao = 'Não foi possível alterar a senha.';
          }
        });
      },
      error: (error) => {
        this.autenticando = false;
        console.error('❌ [LoginPage] Configuração pública ausente para troca de senha.', error);
        this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
      }
    });
  }

  private getTokenFromResponse(response: unknown): string {
    if (!response || typeof response !== 'object') {
      return '';
    }

    const responseObj = response as Record<string, unknown>;

    if (typeof responseObj['Token'] === 'string' && responseObj['Token'].trim().length > 0) {
      return responseObj['Token'].trim();
    }

    return '';
  }

  private isWithinPasswordChangePropagation(): boolean {
    if (this.passwordChangedAt === null) {
      return false;
    }
    const elapsed = Date.now() - this.passwordChangedAt;
    // Permite apenas uma retentativa automática
    this.passwordChangedAt = null;
    return elapsed < 30_000;
  }

  private handleInvalidLogin(): void {
    this.usuarioErro = true;
    this.usuarioCpfInvalido = false;
    this.senhaErro = true;
    this.mensagemAutenticacao = 'Não foi possível validar o usuário.';
    this.router.navigate(['/login']);
  }

  onUsuarioFocus() {
    this.usuarioFocado = true;
    this.limparErroUsuario();
  }

  onUsuarioBlur() {
    this.usuarioFocado = false;
  }

  onSenhaFocus() {
    this.senhaFocada = true;
    this.limparErroSenha();
  }

  onSenhaBlur() {
    this.senhaFocada = false;
  }

  limparErroUsuario() {
    this.usuarioErro = false;
    this.usuarioCpfInvalido = false;
    this.mensagemAutenticacao = '';
  }

  limparErroSenha() {
    this.senhaErro = false;
    this.mensagemAutenticacao = '';
  }

  onNovaSenhaFocus() {
    this.novaSenhaFocada = true;
    this.limparErroNovaSenha();
  }

  onNovaSenhaBlur() {
    this.novaSenhaFocada = false;
  }

  onConfirmarNovaSenhaFocus() {
    this.confirmarNovaSenhaFocada = true;
    this.limparErroConfirmarNovaSenha();
  }

  onConfirmarNovaSenhaBlur() {
    this.confirmarNovaSenhaFocada = false;
  }

  limparErroNovaSenha() {
    this.novaSenhaErro = false;
    this.mensagemAutenticacao = '';
  }

  limparErroConfirmarNovaSenha() {
    this.confirmarNovaSenhaErro = false;
    this.mensagemAutenticacao = '';
  }

  toggleMostrarSenha() {
    this.mostrarSenha = !this.mostrarSenha;
  }

  alterarSenha() {
    this.modoAlteracaoSenha = true;
    this.senhaErro = false;
    this.novaSenhaErro = false;
    this.confirmarNovaSenhaErro = false;
    this.mensagemAutenticacao = '';
  }

  cancelarAlteracaoSenha() {
    this.modoAlteracaoSenha = false;
    this.senha = '';
    this.novaSenha = '';
    this.confirmarNovaSenha = '';
    this.senhaErro = false;
    this.novaSenhaErro = false;
    this.confirmarNovaSenhaErro = false;
    this.mensagemAutenticacao = '';
  }

  esqueciSenha() {
    const cpf = this.sanitizarCpf(this.usuario);

    if (!cpf || !this.validarCpf(cpf)) {
      this.usuarioErro = true;
      this.usuarioCpfInvalido = true;
      this.mensagemAutenticacao = 'Informe um CPF válido para recuperar a senha.';
      return;
    }

    this.usuarioErro = false;
    this.usuarioCpfInvalido = false;
    this.mensagemAutenticacao = '';

    this.autenticando = true;
    this.configService.loadPublicConfig().subscribe({
      next: (publicConfig) => {
        const bffUrl = String(publicConfig.bffUrl || '').trim();
        if (bffUrl === '') {
          this.autenticando = false;
          this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
          return;
        }

        const enableDebugMode = this.configService.isDebugEnabled();
        const endpoint = `${resolveBffUrl(bffUrl, enableDebugMode)}/api/token/reiniciarsenha/${cpf}`;

        this.http.get<boolean>(endpoint).subscribe({
          next: (result) => {
            this.autenticando = false;

            if (result === true) {
              this.exibirPopupRecuperarSenha = true;
              this.mensagemAutenticacao = '';
              return;
            }

            this.mensagemAutenticacao = 'Não foi possível recuperar a senha.';
          },
          error: () => {
            this.autenticando = false;
            this.mensagemAutenticacao = 'Não foi possível recuperar a senha.';
          }
        });
      },
      error: (error) => {
        this.autenticando = false;
        console.error('❌ [LoginPage] Configuração pública ausente para reinício de senha.', error);
        this.mensagemAutenticacao = 'Configuração do sistema indisponível. Tente novamente em instantes.';
      }
    });
  }

  fecharPopupRecuperarSenha(): void {
    this.exibirPopupRecuperarSenha = false;
  }

  private sanitizarCpf(value: string): string {
    return (value || '').replace(/\D/g, '');
  }

  private validarCpf(cpf: string): boolean {
    if (!cpf || cpf.length !== 11) {
      return false;
    }

    if (/^(\d)\1{10}$/.test(cpf)) {
      return false;
    }

    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += Number(cpf.charAt(i)) * (10 - i);
    }

    let resto = (soma * 10) % 11;
    if (resto === 10) {
      resto = 0;
    }

    if (resto !== Number(cpf.charAt(9))) {
      return false;
    }

    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += Number(cpf.charAt(i)) * (11 - i);
    }

    resto = (soma * 10) % 11;
    if (resto === 10) {
      resto = 0;
    }

    return resto === Number(cpf.charAt(10));
  }
}
